import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography
} from "@mui/material";
import { useTranslation } from "_metronic/i18n/language";
import Loading from "app/pages/home/components/Loading";
import { AnanseApiService } from "app/services/Ananse/AnanseApiService";
import React, { useState } from "react";
import Swal from "sweetalert2";
import { InputMultFiles } from "./components";
import ImportReport from "./components/ImportReport";
import {
  AccountType,
  ImportItem,
  JsonResultType,
  QuizAnswerType
} from "./types";
import { fetchAddress } from "./utils/geolocation";

const api = new AnanseApiService();

const normalizeDocument = (value?: string | null) =>
  (value ?? "").replace(/\D/g, "");

const findAccount = (accounts: AccountType[], account: AccountType) => {
  const accountId = account.accountId ?? account.id;
  if (accountId !== null && accountId !== undefined && accountId !== "") {
    const byId = accounts.find(candidate => {
      const candidateId = candidate.accountId ?? candidate.id;
      return String(candidateId) === String(accountId);
    });
    if (byId) return byId;
  }

  const cpf = normalizeDocument(account.cpf);
  if (cpf) {
    const byCpf = accounts.find(
      candidate => normalizeDocument(candidate.cpf) === cpf
    );
    if (byCpf) return byCpf;
  }

  const email = account.email?.trim().toLowerCase();
  if (email) {
    return accounts.find(
      candidate => candidate.email?.trim().toLowerCase() === email
    );
  }

  return undefined;
};

const accountNumericId = (account?: AccountType) => {
  const value = account?.accountId ?? account?.id;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const errorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.response?.data ||
  error?.message ||
  "Erro desconhecido";

export const ImportScreen: React.FC = () => {
  const translate = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [items, setItems] = useState<ImportItem[]>([]);
  const [accounts, setAccounts] = useState<AccountType[]>([]);
  const [quizAnswers, setQuizAnswers] = useState<QuizAnswerType[]>([]);
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validated, setValidated] = useState(false);
  const [progress, setProgress] = useState(0);
  const [operationError, setOperationError] = useState("");

  const clearImport = () => {
    setFiles([]);
    setItems([]);
    setAccounts([]);
    setQuizAnswers([]);
    setValidated(false);
    setProgress(0);
    setOperationError("");
  };

  const handleLoaded = (selectedFiles: File[], loadedItems: ImportItem[]) => {
    setFiles(selectedFiles);
    setItems(loadedItems);
    setAccounts([]);
    setQuizAnswers([]);
    setValidated(false);
    setOperationError("");
    setProgress(0);
  };

  const validateItems = async () => {
    const candidates = items.filter(
      item => item.status !== "INVALID" && item.data
    );
    if (!candidates.length) {
      setOperationError("Nenhum resultado válido foi encontrado nos arquivos.");
      return;
    }

    setValidating(true);
    setProgress(0);
    setOperationError("");
    try {
      const userIds = Array.from(
        new Set(
          candidates
            .map(item => item.data?.account?.userId)
            .filter((id): id is number => !!id)
        )
      );

      const responses = await Promise.all(
        userIds.map(async userId => {
          const [userAccounts, userResults] = await Promise.all([
            api.makeHttpRequest({
              method: "GET",
              url: `/account/buscarporiduser/${userId}`
            }),
            api.makeHttpRequest({
              method: "GET",
              url: `/quiz/userid/${userId}`
            })
          ]);
          return {
            accounts: (userAccounts ?? []) as AccountType[],
            results: (userResults ?? []) as QuizAnswerType[]
          };
        })
      );

      const loadedAccounts = responses.flatMap(response => response.accounts);
      const loadedResults = responses.flatMap(response => response.results);
      const existingResultIds = new Set(
        loadedResults
          .map(result => result.testResultId?.toLowerCase())
          .filter(Boolean)
      );

      const validatedItems = items.map(item => {
        if (item.status === "INVALID" || !item.data) return item;

        const existingAccount = findAccount(loadedAccounts, item.data.account);
        const resolvedAccountId = accountNumericId(existingAccount);
        const resultExists = existingResultIds.has(
          item.data.testeResult.testResultId.toLowerCase()
        );

        return {
          ...item,
          accountExists: !!existingAccount,
          resultExists,
          resolvedAccountId,
          status: resultExists ? "ALREADY_SYNCED" : "READY",
          message: resultExists
            ? "O resultado já estava sincronizado no servidor."
            : existingAccount
            ? "Conta localizada; o resultado ainda precisa ser sincronizado."
            : "Conta e resultado ainda precisam ser sincronizados."
        } as ImportItem;
      });

      setAccounts(loadedAccounts);
      setQuizAnswers(loadedResults);
      setItems(validatedItems);
      setValidated(true);
      setProgress(100);
    } catch (error) {
      setValidated(false);
      setOperationError(
        "Não foi possível consultar o servidor. A sincronização foi bloqueada para evitar registros duplicados."
      );
    } finally {
      setValidating(false);
      setTimeout(() => setProgress(0), 300);
    }
  };

  const buildAccountPayload = (data: JsonResultType) => ({
    userId: data.account.userId,
    communication: data.account.communication ?? false,
    phone: data.account.phone ?? "",
    cpf: data.account.cpf,
    email: data.account.email ?? "",
    docType: data.account.docType ?? "CPF",
    birthDate: data.account.birthDate,
    nextLayout: data.account.nextLayout ?? 0,
    name: data.account.name,
    genre: data.account.genre ?? ""
  });

  const synchronizeItems = async () => {
    const readyCount = items.filter(item => item.status === "READY").length;
    if (!readyCount) return;

    const confirmation = await Swal.fire({
      title: "Confirmar sincronização?",
      text: `${readyCount} resultado(s) serão enviados para o servidor.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sincronizar",
      cancelButtonText: "Cancelar"
    });
    if (!confirmation.isConfirmed) return;

    setSaving(true);
    setOperationError("");
    setProgress(0);

    const workingItems = items.map(item => ({ ...item }));
    const knownAccounts = [...accounts];
    const knownResultIds = new Set(
      quizAnswers
        .map(answer => answer.testResultId?.toLowerCase())
        .filter(Boolean)
    );
    const addressCache = new Map<string, string>();
    let processed = 0;

    const publishItem = (index: number, changes: Partial<ImportItem>) => {
      workingItems[index] = { ...workingItems[index], ...changes };
      setItems([...workingItems]);
    };

    for (let index = 0; index < workingItems.length; index++) {
      const item = workingItems[index];
      if (item.status !== "READY" || !item.data) continue;

      publishItem(index, { status: "IMPORTING", message: "Sincronizando..." });
      let accountId = item.resolvedAccountId;
      let accountCreated = false;

      try {
        if (!accountId) {
          const existingAccount = findAccount(knownAccounts, item.data.account);
          accountId = accountNumericId(existingAccount);
        }

        if (!accountId) {
          const response = await api.makeHttpRequest({
            method: "POST",
            url: "/account",
            data: buildAccountPayload(item.data)
          });
          accountId = Number(response?.accountId);
          if (!Number.isFinite(accountId) || accountId <= 0) {
            throw new Error("A API não retornou o ID da conta criada");
          }
          accountCreated = true;
          knownAccounts.push({ ...item.data.account, accountId });
          publishItem(index, { resolvedAccountId: accountId });
        }

        const testResultId = item.data.testeResult.testResultId.toLowerCase();
        if (knownResultIds.has(testResultId)) {
          publishItem(index, {
            status: "ALREADY_SYNCED",
            resultExists: true,
            message: "Resultado ignorado porque já foi sincronizado.",
            processedAt: new Date().toISOString()
          });
          continue;
        }

        const quizAnswerPayload: QuizAnswerType[] = [];
        for (const answer of item.data.testeResult.quizAnswer) {
          let location = answer.location;
          if (location) {
            if (!addressCache.has(location)) {
              addressCache.set(location, await fetchAddress(location));
            }
            location = addressCache.get(location) ?? location;
          }

          quizAnswerPayload.push({
            rightAnswer: answer.rightAnswer,
            answer: answer.answer,
            quizId: answer.quizId,
            question: answer.question,
            endDateTime: answer.endDateTime,
            initDate: answer.initDate,
            userId: answer.userId,
            accountId,
            location,
            macAddress: answer.macAddress,
            professionalId: answer.professionalId ?? "",
            doctor: answer.doctor ?? null,
            email: answer.email ?? null,
            testResultId: answer.testResultId,
            shots: answer.shots,
            otherAnswers: answer.otherAnswers ?? ""
          });
        }

        await api.makeHttpRequest({
          method: "POST",
          url: "/quiz/quizanswer",
          data: quizAnswerPayload
        });
        knownResultIds.add(testResultId);
        publishItem(index, {
          status: "IMPORTED",
          accountExists: !accountCreated,
          resultExists: false,
          message: accountCreated
            ? "Conta criada e resultado sincronizado com sucesso."
            : "Resultado sincronizado com sucesso na conta existente.",
          processedAt: new Date().toISOString()
        });
      } catch (error) {
        publishItem(index, {
          status: accountCreated ? "PARTIAL" : "FAILED",
          message: accountCreated
            ? `A conta foi criada, mas o resultado falhou: ${errorMessage(error)}`
            : `Falha na sincronização: ${errorMessage(error)}`,
          processedAt: new Date().toISOString()
        });
      } finally {
        processed += 1;
        setProgress(Math.round((processed / readyCount) * 100));
      }
    }

    setItems([...workingItems]);
    setSaving(false);
    Swal.fire(
      "Sincronização concluída",
      "Confira o relatório para identificar os itens enviados e as eventuais falhas.",
      "success"
    );
  };

  const busy = validating || saving;
  const readyCount = items.filter(item => item.status === "READY").length;
  const invalidCount = items.filter(item => item.status === "INVALID").length;
  const alreadySyncedCount = items.filter(
    item => item.status === "ALREADY_SYNCED"
  ).length;
  const importedCount = items.filter(item => item.status === "IMPORTED").length;
  const failedCount = items.filter(
    item => item.status === "FAILED" || item.status === "PARTIAL"
  ).length;

  return (
    <div className="mt-3">
      <Loading isLoading={busy} progress={progress} />
      <InputMultFiles
        files={files}
        disabled={busy}
        onLoaded={handleLoaded}
        onClean={clearImport}
      />

      {operationError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {operationError}
        </Alert>
      )}
      {!!invalidCount && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {invalidCount} item(ns) possui(em) erro de estrutura e não será(ão)
          sincronizado(s). Consulte os detalhes no relatório.
        </Alert>
      )}

      {!!items.length && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              gap={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">Conferência e sincronização</Typography>
                <Typography variant="body2" color="text.secondary">
                  {validated
                    ? "Dados conferidos. Revise o relatório antes de sincronizar."
                    : "Valide os dados para verificar pacientes e resultados já cadastrados."}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
                  <Chip size="small" label={`${items.length} resultado(s)`} />
                  {!!readyCount && (
                    <Chip
                      size="small"
                      color="info"
                      variant="outlined"
                      label={`${readyCount} pronto(s)`}
                    />
                  )}
                  {!!alreadySyncedCount && (
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label={`${alreadySyncedCount} já sincronizado(s)`}
                    />
                  )}
                  {!!importedCount && (
                    <Chip
                      size="small"
                      color="success"
                      label={`${importedCount} enviado(s)`}
                    />
                  )}
                  {!!invalidCount && (
                    <Chip
                      size="small"
                      color="error"
                      variant="outlined"
                      label={`${invalidCount} inválido(s)`}
                    />
                  )}
                  {!!failedCount && (
                    <Chip
                      size="small"
                      color="error"
                      label={`${failedCount} com falha`}
                    />
                  )}
                </Stack>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                gap={1.5}
                sx={{ flexShrink: 0 }}
              >
                <Button
                  variant={validated ? "outlined" : "contained"}
                  color="info"
                  disabled={busy}
                  onClick={validateItems}
                  sx={{ minWidth: 170 }}
                >
                  {validating
                    ? "Validando..."
                    : validated
                    ? "Validar novamente"
                    : translate("buttons_validateJson")}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  disabled={busy || !validated || !readyCount}
                  onClick={synchronizeItems}
                  sx={{ minWidth: 190, color: "#fff" }}
                >
                  {saving
                    ? "Sincronizando..."
                    : `${translate("buttons_syncJsons")} (${readyCount})`}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      <ImportReport items={items} />
    </div>
  );
};
